import { useCallback } from "react";
import { useDashboardContext } from "../contexts/dashboard-context";
import { useDefinition } from "./useDefinition";
import { useEditor } from "./useEditor";
import { useSupportedProductStartDate } from "./useSupportedProductStartDate";

export const useProductStartDates = (): [
	eXpress.DateMonthString[],
	((startDate: eXpress.DateMonthString) => Promise<void>) | undefined,
] => {
	const definition = useDefinition();
	const dashboard = useDashboardContext();
	const editor = useEditor();
	const isStartDateSupported = useSupportedProductStartDate();

	const changeStartDate = useCallback(
		async (startDate: eXpress.DateMonthString) => {
			if (!dashboard.canApplyStartMonth(startDate)) return undefined;

			try {
				await dashboard.applyStartMonthAsync(startDate);
				return undefined;
			} catch (e) {
				const exception = new eXpress.Exception(
					`Couldn't change product's start ${isStartDateSupported}. [value: ${startDate}]`,
					e,
				);
				const convertedException = eXpress.Exception.getMessages(exception).join("\n");
				editor.logger.error(convertedException);
				editor.client.logErrorAsync(exception, false);
				return undefined;
			}
		},
		[dashboard, editor.client, editor.logger, isStartDateSupported],
	);

	if (!definition || !definition.startMonth || !isStartDateSupported) return [[], undefined];

	const definitionExtension = new eXpress.core.DefinitionStartMonthExtensions(definition);
	const definitionStartDates =
		isStartDateSupported === "year"
			? definitionExtension.getStartYears()
			: definitionExtension.getStartMonths();

	const mappedStartDates = definitionStartDates.map((x) =>
		eXpress.DateMonthString.fromString(
			`${x.getFullYear()}${String(x.getMonth() + 1).padStart(2, "0")}`,
		),
	);

	return [mappedStartDates, changeStartDate];
};
